import type {
    ConsentSettings
} from '#x1-base/types';


export interface UsePrivacySettingsUtilsOptions {}
export function usePrivacySettingsUtils(options: Partial<UsePrivacySettingsUtilsOptions> = {}) {

    const serializedStringDelimiter = '-';
    const serializedKeyValueDelimiter = '.';
    const cookieName = 'tracking-consent';
    const cookieMaxAge = 60 * 60 * 24 * 45; // 45 days

    const settingsDefaults: ConsentSettings = {
        e: true,
        a: false,
        m: false
    };

    const {
        useReactiveKookie
    } = useKookies();

    const consentCookie = useReactiveKookie<Nullable<string>>(cookieName, {
        maxAge: cookieMaxAge,
        path: '/',
        sameSite: 'lax'
    });

    const deserializedSettings: ConsentSettings = consentCookie.value
        ? deserializeCookie()
        : { ...settingsDefaults };
    const settings: ConsentSettings = reactive(deserializedSettings);

    const analyticsConsentTrigger = useScriptTriggerConsent()
    const marketingConsentTrigger = useScriptTriggerConsent()
    const { consent: analyticsConsent } = useScriptGoogleAnalytics({
        scriptOptions: {
            trigger: analyticsConsentTrigger
        }
    });
    const { consent: metaPixelConsent } = useScriptMetaPixel({
        scriptOptions: {
            trigger: marketingConsentTrigger
        }
    });

    function getSerializeSettings(): string {
        const serialized = [];
        for (const key in settings) {
            const value = Number(settings[key as keyof ConsentSettings]);
            serialized.push(`${key[0]}${serializedKeyValueDelimiter}${value}`);
        }

        return serialized.join(serializedStringDelimiter);
    }

    function deserializeCookie(): ConsentSettings {
        const pairs = consentCookie.value?.split(serializedStringDelimiter) || [];
        const settings: ConsentSettings = { ...settingsDefaults };

        pairs.forEach(pair => {
            const [key, value] = pair.split(serializedKeyValueDelimiter);
            if (key && value) {
                const settingKey = Object.keys(settings).find(k => k[0] === key);

                if (settingKey) {
                    settings[settingKey as keyof ConsentSettings] = Boolean(Number(value));
                }
            }
        });

        return settings;
    }

    function setAnalyticsConsent(isGranted: boolean): void {
        const state = isGranted ? 'granted' : 'denied';

        analyticsConsent?.update({
            ad_storage: state,
            ad_user_data: state,
            ad_personalization: state,
            analytics_storage: state
        });

        if (isGranted) {
            analyticsConsentTrigger.accept();
        } else {
            analyticsConsentTrigger.revoke();
        }
    }

    function setMarketingConsent(isGranted: boolean): void {
        // Only send consent commands once fbevents.js is running (it replaces the
        // stub's queueing with `fbq.callMethod`). Until then the consent trigger
        // gates loading, and a queued `fbq('consent', 'revoke')` makes fbevents
        // stop draining its queue, so a `grant` queued behind it is never reached
        // and the pixel never fires.
        const fbq = (globalThis as { fbq?: { callMethod?: unknown } }).fbq;
        const isPixelLoaded = typeof fbq?.callMethod === 'function';

        if (isGranted) {
            if (isPixelLoaded) {
                metaPixelConsent?.grant();
            }
            marketingConsentTrigger.accept();
        } else {
            if (isPixelLoaded) {
                metaPixelConsent?.revoke();
            }
            marketingConsentTrigger.revoke();
        }
    }

    // Push the current settings to the underlying tracking scripts.
    function applyConsent(): void {
        setAnalyticsConsent(settings.a);
        setMarketingConsent(settings.m);
    }

    // Persist the current settings to the cookie and apply them immediately.
    function saveConsent(): void {
        consentCookie.value = getSerializeSettings();
        applyConsent();
    }


    return {
        getSerializeSettings,
        deserializeCookie,
        consentCookie,
        settingsDefaults,
        settings,
        analyticsConsent,
        metaPixelConsent,
        setAnalyticsConsent,
        setMarketingConsent,
        applyConsent,
        saveConsent
    };
}
